/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import React, { useRef } from "react"
import { useTheme } from "@cd2/theme"
import Tippy from "@tippyjs/react"
import { IComponentProp, isLight } from "@cd2/tools"
import { colors } from "./EOSThemeProvider"

interface IProps {
  children: React.ReactNode
  content: React.ReactNode
  className?: string
  padding?: number
  showBorder?: boolean
  color?: string
  disabled?: boolean
  interactive?: boolean
  zIndex?: number
  appendTo?: IComponentProp<typeof Tippy, "appendTo">
  width?: string
  maxWidth?: string
  delay?: IComponentProp<typeof Tippy, "delay">
  placement?: IComponentProp<typeof Tippy, "placement">
}

export function QuickToolTip({
  children,
  content,
  className,
  padding = 12,
  showBorder,
  interactive,
  color = "#333",
  disabled,
  zIndex,
  appendTo,
  width,
  maxWidth,
  delay,
  placement = "bottom",
}: IProps) {
  const ref = useRef<HTMLDivElement>(null)
  const theme = useTheme()
  const mountTo = document.getElementById("tippy-node") || "parent"

  return (
    <div
      ref={ref}
      css={css`
        display: inline-block;
        ${width ? `width: ${width};` : ""};
      `}
    >
      <Tippy
        appendTo={appendTo ?? mountTo}
        content={<div>{content}</div>}
        disabled={disabled}
        interactive={interactive}
        zIndex={zIndex}
        delay={delay}
        css={css`
          &.tippy-box[data-animation="fade"][data-state="hidden"] {
            opacity: 0;
          }

          [data-tippy-root] {
            max-width: calc(100vw - 10px);
          }

          &.tippy-box {
            ${maxWidth ? `max-width: ${maxWidth}!important;` : ""};
            background-color: ${color};
            color: ${isLight(color) ? "#333" : "white"};
            border-radius: 4px;
            font-size: 13px;
            line-height: 1.4;
            outline: 0;
            transition-property: transform, visibility, opacity;
            box-shadow: ${theme.boxShadow.sm};
          }

          &.tippy-box[data-placement^="bottom"] > .tippy-arrow {
            top: 0;
          }

          &.tippy-box[data-placement^="bottom"] > .tippy-arrow:before {
            top: -7px;
            left: 0;
            border-width: 0 8px 8px;
            border-bottom-color: initial;
            transform-origin: center bottom;
          }

          &.tippy-box[data-placement^="right"] > .tippy-arrow {
            left: 0;
          }

          &.tippy-box[data-placement^="right"] > .tippy-arrow:before {
            left: -7px;
            border-width: 8px 8px 8px 0;
            border-right-color: initial;
            transform-origin: center right;
          }

          &.tippy-box[data-inertia][data-state="visible"] {
            transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
          }

          .tippy-arrow {
            width: 16px;
            height: 16px;
            color: ${color};
            filter: drop-shadow(0 -1.5px 0.5px rgb(0 0 0 / 5%));
          }

          .tippy-arrow:before {
            content: "";
            position: absolute;
            border-color: transparent;
            border-style: solid;
          }

          .tippy-content {
            position: relative;
            z-index: 1;
            padding: ${padding}px;
          }

          &.tippy-box[data-animation="perspective"][data-placement^="bottom"] {
            transform-origin: top;
          }

          &.tippy-box[data-animation="perspective"][data-placement^="bottom"][data-state="visible"] {
            transform: perspective(700px);
          }

          &.tippy-box[data-animation="perspective"][data-placement^="bottom"][data-state="hidden"] {
            transform: perspective(700px) translateY(-8px) rotateX(-60deg);
          }

          &.tippy-box[data-animation="perspective"][data-state="hidden"] {
            opacity: 0;
          }
        `}
        placement={placement}
        animation="perspective"
      >
        <div
          className={className}
          css={css(
            css`
              display: inline-block;
              cursor: default;
              ${width ? `width: ${width};` : ""};
            `,
            showBorder &&
              css`
                text-decoration: 1px dashed underline ${colors.lightBorder};
                text-underline-offset: 1px;
              `,
          )}
        >
          {children}
        </div>
      </Tippy>
    </div>
  )
}
QuickToolTip.mountNode = <div id="tippy-node" />
